import React, { memo, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useEscape } from '../../hooks/useEscape';
import { DEPRECATED_SharedPanelProps } from '../../types/DEPRECATED_SharedPanelProps';
import { UserAction } from '../../types/UserAction';
import { Backdrop } from '../Backdrop/Backdrop';
import { Box } from '../Box/Box';
import { ContentContainer } from '../ContentContainer/ContentContainer';
import { Header } from '../Header/Header';
import { Layout } from '../Layout/Layout';
import { Tabs } from '../Tabs/Tabs';

const DEFAULT_PADDING = 6;
const RIGHT_SPACE = '48px';

export const Panel_DEPRECATED = memo(
  ({
    actions = [],
    children,
    chip,
    footer,
    isEmbedded = false,
    isLoading = false,
    isPadded,
    isVisible = false,
    selectedTab: overrideSelectedTab,
    mode,
    tabs,
    title,
    width = 'l',
    onBack,
    onHide,
    onSelectTab,
  }: DEPRECATED_SharedPanelProps) => {
    const intl = useIntl();
    const initialSelectedTab = useMemo(
      () => overrideSelectedTab ?? tabs?.[0]?.value ?? null,
      [overrideSelectedTab, tabs],
    );

    const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
    useEscape(onHide);

    useEffect(() => setSelectedTab(initialSelectedTab), [initialSelectedTab]);

    if (!isVisible) {
      return null;
    }

    const headerActions: UserAction[] = actions.map((action) => ({
      ...action,
      mode: 'icon',
      text: action.label,
      onClick: () => action.onClick?.(action.value as any),
    }));

    if (!isEmbedded) {
      headerActions.push({
        mode: 'icon',
        icon: 'x',
        text: intl.formatMessage({
          id: 'PanelDeprecated.close',
          defaultMessage: 'Close',
          description: 'Close panel button label',
        }),
        tooltip: intl.formatMessage({
          id: 'PanelDeprecated.closeTooltip',
          defaultMessage: 'Close',
          description: 'Tooltip for close panel button',
        }),
        onClick: onHide,
      });
    }

    const header = (
      <Header
        actions={headerActions}
        chips={chip ? [chip] : undefined}
        title={title}
        backAction={
          onBack
            ? {
                text: intl.formatMessage({
                  id: 'PanelDeprecated.back',
                  defaultMessage: 'Back',
                  description: 'Back button label',
                }),
                tooltip: intl.formatMessage({
                  id: 'PanelDeprecated.backTooltip',
                  defaultMessage: 'Back to previous',
                  description: 'Tooltip for back button',
                }),
                icon: 'arrow-left',
                onClick: onBack,
              }
            : undefined
        }
      />
    );

    const panel = (
      <Layout
        as="aside"
        direction="column"
        styles={componentStyles.panel({ isEmbedded, width })}
      >
        {header}
        <ContentContainer loadingContent={{ isLoading }}>
          <>
            {selectedTab && tabs && (
              <Tabs
                enableBorder
                enableContentPadding={false}
                panelHeight="100%"
                selectedTab={selectedTab}
                tabs={tabs}
                onSelectTab={(updatedTab) => {
                  onSelectTab?.(updatedTab);
                  setSelectedTab(updatedTab);
                }}
              />
            )}
            {children && (
              <Box
                p={isPadded && !tabs ? DEFAULT_PADDING : 0}
                styles={componentStyles.contents}
              >
                {children}
              </Box>
            )}
            {footer}
          </>
        </ContentContainer>
      </Layout>
    );

    switch (mode) {
      case 'blocking':
        return <Backdrop disableBodyScroll={isVisible}>{panel}</Backdrop>;
      default:
        return panel;
    }
  },
);

const componentStyles = {
  contents: {
    flex: 'auto',
    overflowY: 'auto',
  },
  // * isEmbedded and width are used here because of `fixed` position in combination with `Sidebar`.
  panel: ({
    isEmbedded,
    width,
  }: {
    isEmbedded: boolean;
    width: 'm' | 'l' | 'fill';
  }) => ({
    backgroundColor: 'background',
    border: 'none',
    boxShadow: 'raised',
    bottom: 0,
    height: 'auto',
    position: 'fixed',
    right: 0,
    top: 'DEPRECATED_navbar.height',
    width: getWidth(width),
    zIndex: 'panel',
    ...(isEmbedded
      ? {
          border: 'border.divider',
          height: '100%',
          position: 'static',
          width: getEmbeddedWidth(width),
        }
      : {}),
  }),
};

const getEmbeddedWidth = (width: string) => {
  switch (width) {
    case 'fill':
      return '100%';
    case 'm':
      return 'panel.width.m';
    default:
      return 'panel.width.l';
  }
};

const getWidth = (width: string) => {
  switch (width) {
    case 'fill':
      return `calc(100% - ${RIGHT_SPACE})`;
    case 'm':
      return 'panel.width.m';
    default:
      return 'panel.width.l';
  }
};
